<template>
	<section class="ip">
		<b-overlay :show="loading" no-wrap rounded></b-overlay>

		<template v-if="!loading && data">
			<!-- Header card -->
			<div class="ip-card mb-3">
				<div class="ip-card__body">
					<div class="d-flex align-items-start justify-content-between">
						<div>
							<div class="ip-channel">{{ data.booking.channel }}</div>
							<div class="ip-order-id">#{{ data.booking.channelOrderId }}</div>
						</div>
						<div class="d-flex flex-column align-items-end">
							<b-badge :variant="statusVariant" class="ip-status mb-1">{{ data.booking.status }}</b-badge>
							<a v-if="routeUrl" :href="routeUrl" target="_blank" class="ip-route-link">
								<md-icon icon="directions" style="font-size:13px;" /> Ver ruta
							</a>
						</div>
					</div>
					<div class="ip-dates mt-2">
						<span>Creado <strong>{{ formatUtcShort(data.booking.createdAt) }}</strong></span>
						<span class="ip-dates__sep">·</span>
						<span>Confirmado <strong>{{ formatUtcShort(data.booking.confirmedAt) }}</strong></span>
						<span class="ip-dates__sep">·</span>
						<span>Intentos <strong>{{ data.booking.attempts }}</strong></span>
						<span v-if="data.booking.failedAt" class="ip-dates__sep">·</span>
						<span v-if="data.booking.failedAt" class="text-danger">Falló <strong>{{ formatUtcShort(data.booking.failedAt) }}</strong></span>
					</div>
				</div>
			</div>

			<!-- Actions card -->
			<div v-if="data.booking.availableActions && data.booking.availableActions.length" class="ip-card ip-card--actions mb-3">
				<div class="ip-card__header">Acciones</div>
				<div class="ip-card__body">
					<b-alert v-if="actionError" show variant="danger" dismissible class="py-2 small mb-3" @dismissed="actionError = null">
						{{ actionError }}
					</b-alert>
					<div class="ip-actions">
						<b-button
							v-for="action in data.booking.availableActions"
							:key="action.key"
							variant="outline-secondary"
							:disabled="actionLoading !== null"
							class="ip-action-btn"
							@click="confirmAction(action)"
						>
							<b-spinner v-if="actionLoading === action.key" small class="mr-1" />
							<md-icon v-else :icon="destructiveActions.includes(action.key) ? 'cancel' : 'play_circle_filled'" style="font-size:15px;" />
							{{ action.label }}
						</b-button>
					</div>
				</div>
			</div>

			<b-modal
				ref="confirm-action-modal"
				title="Confirmar acción"
				centered
				size="sm"
				hide-header-close
				ok-variant="secondary"
				ok-title="Continuar"
				cancel-title="Cancelar"
				@ok="executeAction(pendingAction.key)"
				@hidden="pendingAction = null"
			>
				<p class="mb-0">¿Deseas continuar con la acción <strong>{{ pendingAction && pendingAction.label }}</strong>?</p>
			</b-modal>

			<!-- Timeline card -->
			<div class="ip-card">
				<div class="ip-card__header d-flex align-items-center justify-content-between">
					Actividad
					<span class="ip-utc-note">Fechas en UTC</span>
				</div>
				<div class="ip-card__body ip-card__body--flush">
					<div class="ip-timeline">
						<div
							v-for="(event, i) in timeline"
							:key="i"
							class="ip-event"
							:class="{
								'ip-event--failed': event.failed,
								'ip-event--active': activeIndex === i,
								'ip-event--last': i === timeline.length - 1
							}"
							@click="toggle(i)"
						>
							<!-- Indicator -->
							<div class="ip-event__track">
								<div class="ip-event__dot" :class="dotClass(event)"></div>
								<div class="ip-event__line" v-if="i < timeline.length - 1"></div>
							</div>

							<!-- Content -->
							<div class="ip-event__content">
								<div class="ip-event__row">
									<span class="ip-event__time">{{ formatUtcTime(event.time) }}</span>
									<span class="ip-event__name">{{ event.name }}</span>
									<div class="ip-event__tags">
										<span class="ip-tag" :class="event.direction === 'inbound' ? 'ip-tag--inbound' : 'ip-tag--outbound'">
											<md-icon :icon="event.direction === 'inbound' ? 'call_received' : 'call_made'" style="font-size:11px;" />
											{{ event.direction }}
										</span>
										<span v-if="event.status" class="ip-tag" :class="event.failed ? 'ip-tag--error' : 'ip-tag--success'">
											{{ event.status }}
										</span>
										<span v-if="event.attempts > 1" class="ip-tag ip-tag--warning">
											{{ event.attempts }} intentos
										</span>
									</div>
									<md-icon class="ip-event__chevron" :icon="activeIndex === i ? 'expand_less' : 'expand_more'" style="font-size:16px;" />
								</div>

								<b-collapse :visible="activeIndex === i">
									<div class="ip-event__detail">
										<div class="ip-detail-meta">{{ event.time }} UTC</div>

										<template v-if="event.response">
											<div class="ip-detail-label">REQUEST</div>
											<div class="ip-code">
												<pre class="m-0">{{ JSON.stringify(event.payload, null, 2) }}</pre>
											</div>
											<div class="ip-detail-label mt-2">RESPONSE</div>
											<div class="ip-code" :class="event.failed ? 'ip-code--error' : 'ip-code--success'">
												<pre class="m-0">{{ JSON.stringify(event.response, null, 2) }}</pre>
											</div>
											<template v-if="event.attemptLogs && event.attemptLogs.length">
												<div class="ip-detail-label mt-3">INTENTOS ({{ event.attemptLogs.length }})</div>
												<div class="ip-attempts">
													<div v-for="(attempt, j) in event.attemptLogs" :key="j" class="ip-attempt">
														<div class="ip-attempt__header">
															<span class="ip-attempt__num">#{{ attempt.attempt }}</span>
															<span class="ip-attempt__time">{{ formatUtc(attempt.at) }}</span>
															<b-badge :variant="attempt.error ? 'danger' : 'success'" class="ml-auto" style="font-size:0.62rem;">
																{{ attempt.error ? 'error' : 'ok' }}
															</b-badge>
														</div>
														<div class="ip-code mt-1" :class="attempt.error ? 'ip-code--error' : ''">
															<pre class="m-0">{{ attempt.error || JSON.stringify(attempt.response, null, 2) }}</pre>
														</div>
													</div>
												</div>
											</template>
										</template>
										<template v-else>
											<div class="ip-detail-label">PAYLOAD</div>
											<div class="ip-code">
												<pre class="m-0">{{ JSON.stringify(event.payload, null, 2) }}</pre>
											</div>
										</template>
									</div>
								</b-collapse>
							</div>
						</div>
					</div>
				</div>
			</div>

		</template>

		<div v-if="!loading && !data" class="text-muted small text-center py-3">
			No hay datos de integración disponibles.
		</div>
	</section>
</template>

<script>
import api from '@/api.js';
import MdIcon from '../../../../helpers/MdIcon.vue';

export default {
	components: { MdIcon },
	props: {
		transportServiceId: {
			type: Number,
			required: true
		}
	},
	data() {
		return {
			loading: true,
			data: null,
			activeIndex: null,
			actionLoading: null,
			actionError: null,
			pendingAction: null,
			destructiveActions: ['cannot_service', 'cancel', 'reject']
		};
	},
	computed: {
		statusVariant() {
			const map = { confirmed: 'success', failed: 'danger', pending: 'warning', cancelled: 'secondary' };
			return map[this.data?.booking?.status] || 'secondary';
		},
		routeUrl() {
			const p = this.data?.booking?.payload;
			if (!p?.duseLocation || !p?.auseLocation) return null;
			return `https://www.google.com/maps/dir/?api=1&origin=${p.duseLocation.latitude},${p.duseLocation.longitude}&destination=${p.auseLocation.latitude},${p.auseLocation.longitude}&travelmode=driving`;
		},
		timeline() {
			if (!this.data) return [];
			const events = [];

			(this.data.inboundLogs || []).forEach(log => {
				events.push({
					time: log.createdAt,
					name: log.event,
					direction: 'inbound',
					payload: log.payload,
					response: null,
					status: null,
					failed: false,
					attempts: 1,
					attemptLogs: []
				});
			});

			(this.data.outboundLogs || []).forEach(log => {
				events.push({
					time: log.createdAt,
					name: log.action,
					direction: 'outbound',
					payload: log.payload,
					response: log.response,
					status: log.status,
					failed: log.status !== 'success',
					attempts: log.attempts,
					attemptLogs: log.attemptLogs || []
				});
			});

			return events.sort((a, b) => b.time.localeCompare(a.time));
		}
	},
	methods: {
		toggle(i) {
			this.activeIndex = this.activeIndex === i ? null : i;
		},
		formatUtc(iso) {
			if (!iso) return '—';
			const d = new Date(iso);
			const pad = n => String(n).padStart(2, '0');
			return `${d.getUTCFullYear()}-${pad(d.getUTCMonth()+1)}-${pad(d.getUTCDate())} `
				+ `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
		},
		formatUtcShort(iso) {
			if (!iso) return '—';
			const d = new Date(iso);
			const pad = n => String(n).padStart(2, '0');
			return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth()+1)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
		},
		formatUtcTime(iso) {
			if (!iso) return '—';
			const d = new Date(iso);
			const pad = n => String(n).padStart(2, '0');
			const months = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
			return `${pad(d.getUTCDate())}/${months[d.getUTCMonth()]} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
		},
		dotClass(event) {
			if (event.direction === 'inbound') return 'ip-event__dot--inbound';
			return event.failed ? 'ip-event__dot--failed' : 'ip-event__dot--success';
		},
		load() {
			this.loading = true;
			api(this)
				.get(`transport-service/${this.transportServiceId}/integration`)
				.then(res => { this.data = res.data.data; })
				.catch(this.$root.handleRequestError)
				.finally(() => { this.loading = false; });
		},
		confirmAction(action) {
			this.pendingAction = action;
			this.$refs['confirm-action-modal'].show();
		},
		executeAction(key) {
			this.actionLoading = key;
			this.actionError = null;
			api(this)
				.post(`transport-service/${this.transportServiceId}/integration/action`, { action: key })
				.catch(err => {
					if (err.response?.status === 422) {
						this.actionError = err.response.data.error;
					} else {
						this.$root.handleRequestError(err);
					}
				})
				.finally(() => {
					this.actionLoading = null;
					this.load();
				});
		}
	},
	mounted() {
		this.load();
	}
};
</script>

<style scoped>
.ip {
	position: relative;
	min-height: 60px;
}

/* Card */
.ip-card {
	border: 1px solid #e2e6ea;
	border-radius: 0.5rem;
	overflow: hidden;
	background: #fff;
}
.ip-card__header {
	padding: 0.5rem 1rem;
	background: #f8f9fa;
	border-bottom: 1px solid #e2e6ea;
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.07em;
	text-transform: uppercase;
	color: #6c757d;
}
.ip-card__body {
	padding: 0.85rem 1rem;
}
.ip-card__body--flush {
	padding: 0;
}
.ip-card--actions {
	border-color: #c8d6e5;
	background: #f0f4f8;
}
.ip-utc-note {
	font-size: 0.6rem;
	font-weight: 400;
	letter-spacing: 0.03em;
	color: #adb5bd;
	text-transform: none;
}
.ip-card--actions .ip-card__header {
	background: #e2eaf3;
	border-color: #c8d6e5;
	color: #3d6b9e;
}

/* Header content */
.ip-channel {
	font-size: 1rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #212529;
}
.ip-order-id {
	font-size: 0.8rem;
	color: #6c757d;
	margin-top: 0.1rem;
}
.ip-status {
	font-size: 0.75rem;
	padding: 0.3em 0.6em;
}
.ip-route-link {
	font-size: 0.75rem;
	color: #6c757d;
	text-decoration: none;
}
.ip-route-link:hover {
	color: #343a40;
}
.ip-dates {
	font-size: 0.75rem;
	color: #6c757d;
}
.ip-dates__sep {
	margin: 0 0.4rem;
}

/* Actions */
.ip-actions {
	display: flex;
	flex-wrap: wrap;
	gap: 0.5rem;
}
.ip-action-btn {
	font-size: 0.8rem;
	padding: 0.35rem 0.85rem;
}

/* Timeline */
.ip-timeline {
	display: flex;
	flex-direction: column;
}
.ip-event {
	display: flex;
	cursor: pointer;
}
.ip-event__track {
	display: flex;
	flex-direction: column;
	align-items: center;
	width: 36px;
	flex-shrink: 0;
	padding-top: 0.75rem;
}
.ip-event__dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex-shrink: 0;
	z-index: 1;
}
.ip-event__dot--inbound { background: #adb5bd; border: 2px solid #6c757d; }
.ip-event__dot--success { background: #28a745; border: 2px solid #1e7e34; }
.ip-event__dot--failed  { background: #dc3545; border: 2px solid #bd2130; }
.ip-event__line {
	width: 2px;
	flex: 1;
	background: #e9ecef;
	min-height: 12px;
	margin-top: 2px;
}
.ip-event__content {
	flex: 1;
	min-width: 0;
	border-bottom: 1px solid #f1f3f5;
}
.ip-event--last .ip-event__content {
	border-bottom: none;
}
.ip-event__row {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.6rem 1rem 0.6rem 0;
	transition: background 0.12s;
}
.ip-event:hover .ip-event__row {
	background: #f8f9fa;
}
.ip-event--active .ip-event__row {
	background: #f1f3f5;
}
.ip-event__time {
	font-size: 0.72rem;
	color: #6c757d;
	white-space: nowrap;
	font-family: monospace;
	min-width: 110px;
}
.ip-event__name {
	font-size: 0.85rem;
	font-weight: 600;
	flex: 1;
	color: #343a40;
}
.ip-event--failed .ip-event__name {
	color: #dc3545;
}
.ip-event__tags {
	display: flex;
	gap: 0.3rem;
	align-items: center;
}
.ip-tag {
	font-size: 0.62rem;
	font-weight: 600;
	letter-spacing: 0.04em;
	text-transform: uppercase;
	padding: 0.15em 0.45em;
	border-radius: 0.2rem;
	display: inline-flex;
	align-items: center;
	gap: 2px;
}
.ip-tag--inbound  { background: #e9ecef; color: #495057; }
.ip-tag--outbound { background: #343a40; color: #fff; }
.ip-tag--success  { background: #d4edda; color: #155724; }
.ip-tag--error    { background: #f8d7da; color: #721c24; }
.ip-tag--warning  { background: #fff3cd; color: #856404; }
.ip-event__chevron {
	color: #adb5bd;
	flex-shrink: 0;
}

/* Detail */
.ip-event__detail {
	padding: 0.5rem 1rem 0.75rem 0;
}
.ip-detail-meta {
	font-size: 0.68rem;
	color: #adb5bd;
	font-family: monospace;
	margin-bottom: 0.6rem;
}
.ip-detail-label {
	font-size: 0.62rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	color: #adb5bd;
	text-transform: uppercase;
	margin-bottom: 0.25rem;
}
.ip-code {
	max-height: 180px;
	overflow-y: auto;
	background: #f8f9fa;
	border-left: 3px solid #dee2e6;
	border-radius: 0 0.25rem 0.25rem 0;
	padding: 0.4rem 0.6rem;
	font-size: 0.72rem;
	font-family: monospace;
}
.ip-code--success { border-left-color: #28a745; background: #f6fff8; }
.ip-code--error   { border-left-color: #dc3545; background: #fff5f5; }

/* Attempts */
.ip-attempts {
	border-left: 3px solid #dee2e6;
	padding-left: 0.75rem;
	margin-top: 0.25rem;
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
}
.ip-attempt {
	background: #fff;
	border: 1px solid #e9ecef;
	border-radius: 0.3rem;
	padding: 0.4rem 0.6rem;
}
.ip-attempt__header {
	display: flex;
	align-items: center;
	gap: 0.4rem;
	margin-bottom: 0.25rem;
}
.ip-attempt__num {
	font-size: 0.72rem;
	font-weight: 700;
	color: #495057;
}
.ip-attempt__time {
	font-size: 0.68rem;
	color: #adb5bd;
	font-family: monospace;
}
</style>
